if (!customElements.get('warranty-form')) {
  class WarrantyForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.alertEl = this.querySelector('.wf-alert');
      this.alertTextEl = this.alertEl ? this.alertEl.querySelector('.wf-alert__text') || this.alertEl : null;
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.orderInput = this.form.querySelector('[name="order_number"]');
      this.emailInput = this.form.querySelector('[name="email"]');
      this.messageInput = this.form.querySelector('[name="message"]');
      this.phoneInput = this.form.querySelector('[name="phone"]');
      this.phoneField = this.querySelector('.wf-phone-field');
      this.smsConsentInput = this.form.querySelector('[name="sms_consent"]');
      this.productInput = this.form.querySelector('[name="product"]');
      this.productField = this.querySelector('.wf-product-field');
      this.itemsWrap = this.querySelector('.wf-items');
      this.itemsList = this.querySelector('.wf-items__list');
      this.lookupStatus = this.querySelector('.wf-lookup-status');
      this.photoInput = this.querySelector('.wf-photo-input');
      this.previews = this.querySelector('.wf-previews');
      this.files = [];
      this.maxFiles = 3;
      this.maxFileBytes = 10 * 1024 * 1024;
      this.uid = Math.random().toString(36).slice(2, 8);
      this.lookupKey = null;
      this.lookupAbort = null;
      this.pickerActive = false;

      const defaultChannel = this.dataset.defaultChannel;
      if (defaultChannel) {
        const radio = this.form.querySelector(`[name="channel"][value="${defaultChannel}"]`);
        if (radio) radio.checked = true;
      }

      this.form.addEventListener('change', (evt) => {
        if (evt.target.name === 'channel') {
          this.syncState();
          if (this.channel === 'amazon') this.deactivatePicker();
          else this.maybeLookup();
        } else if (evt.target.name === 'request_type') {
          this.syncState();
          this.applyPickerDefaults();
        } else if (evt.target.name === 'sms_consent') {
          this.syncState();
        }
      });
      this.orderInput.addEventListener('input', () => this.orderInput.setCustomValidity(''));
      this.orderInput.addEventListener('change', () => this.maybeLookup());
      this.emailInput.addEventListener('change', () => this.maybeLookup());
      if (this.itemsList) this.itemsList.addEventListener('change', () => this.syncPickerValue());
      if (this.photoInput) this.photoInput.addEventListener('change', () => this.addFiles());
      if (this.previews) {
        this.previews.addEventListener('click', (evt) => {
          const btn = evt.target.closest('[data-index]');
          if (!btn) return;
          const removed = this.files.splice(Number(btn.dataset.index), 1)[0];
          if (removed && removed.__previewUrl) URL.revokeObjectURL(removed.__previewUrl);
          this.renderPreviews();
        });
      }
      this.form.addEventListener('submit', (evt) => this.submit(evt));

      this.syncState();
      if (this.dataset.turnstileSitekey) this.loadTurnstile();
    }

    get channel() {
      const checked = this.form.querySelector('[name="channel"]:checked');
      return checked ? checked.value : 'shopify';
    }

    get requestType() {
      const checked = this.form.querySelector('[name="request_type"]:checked');
      return checked ? checked.value : 'registration';
    }

    get lookupEndpoint() {
      const endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
      return endpoint ? `${endpoint}/lookup` : '';
    }

    syncState() {
      const amazon = this.channel === 'amazon';
      this.querySelectorAll('.wf-hint--shopify').forEach((el) => (el.hidden = amazon));
      this.querySelectorAll('.wf-hint--amazon').forEach((el) => (el.hidden = !amazon));
      this.orderInput.placeholder = amazon ? '113-1234567-1234567' : '#1234';
      this.orderInput.setCustomValidity('');

      const claim = this.requestType === 'claim';
      this.querySelectorAll('.wf-claim-only').forEach((el) => (el.hidden = !claim));
      if (this.messageInput) this.messageInput.required = claim;

      // SMS updates need a phone number to send them to.
      if (this.phoneInput) {
        const smsChecked = !!(this.smsConsentInput && this.smsConsentInput.checked);
        this.phoneInput.required = smsChecked;
        if (this.phoneField) this.phoneField.classList.toggle('is-required', smsChecked);
      }
    }

    // --- Order-aware product picker ---------------------------------------

    async maybeLookup() {
      if (this.channel !== 'shopify' || !this.itemsWrap) return;
      const endpoint = this.lookupEndpoint;
      const order = this.orderInput.value.trim();
      const email = this.emailInput.value.trim();
      if (!endpoint || !order || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

      const key = `${order.toLowerCase()}|${email.toLowerCase()}`;
      if (key === this.lookupKey) return;
      this.lookupKey = key;
      this.deactivatePicker();

      if (this.lookupAbort) this.lookupAbort.abort();
      this.lookupAbort = new AbortController();
      try {
        const lookupData = new FormData();
        lookupData.append('order_number', order);
        lookupData.append('email', email);
        const res = await fetch(endpoint, { method: 'POST', body: lookupData, signal: this.lookupAbort.signal });
        const data = await res.json().catch(() => null);
        if (data && data.ok && data.order && data.order.items.length > 0) {
          this.activatePicker(data.order);
        }
      } catch (err) {
        // The picker is a progressive enhancement. On any failure the
        // free-text product field simply stays in place.
      }
    }

    activatePicker(order) {
      this.itemsList.innerHTML = '';
      const preCheck = this.requestType === 'registration';
      order.items.forEach((title, index) => {
        const id = `wf-${this.uid}-item-${index}`;
        const row = document.createElement('div');
        row.className = 'wf-item';
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.className = 'checkbox checkbox--small';
        box.id = id;
        box.value = title;
        box.checked = preCheck;
        const label = document.createElement('label');
        label.className = 'label required-inline';
        label.htmlFor = id;
        label.textContent = title;
        row.append(box, label);
        this.itemsList.append(row);
      });
      this.pickerActive = true;
      this.itemsWrap.hidden = false;
      this.productField.hidden = true;
      this.productInput.required = false;
      if (this.lookupStatus) {
        this.lookupStatus.textContent = `Order ${order.name} found. Select your item(s) below.`;
        this.lookupStatus.hidden = false;
      }
      this.syncPickerValue();
    }

    deactivatePicker() {
      if (this.lookupStatus) this.lookupStatus.hidden = true;
      if (!this.pickerActive) return;
      this.pickerActive = false;
      this.itemsWrap.hidden = true;
      this.itemsList.innerHTML = '';
      this.productField.hidden = false;
      this.productInput.required = true;
      this.productInput.value = '';
    }

    applyPickerDefaults() {
      if (!this.pickerActive) return;
      const preCheck = this.requestType === 'registration';
      this.itemsList.querySelectorAll('input[type="checkbox"]').forEach((box) => (box.checked = preCheck));
      this.syncPickerValue();
    }

    syncPickerValue() {
      if (!this.pickerActive) return;
      const checked = Array.from(this.itemsList.querySelectorAll('input:checked')).map((box) => box.value);
      this.productInput.value = checked.join(', ');
    }

    // --- Photos -------------------------------------------------------------

    addFiles() {
      const incoming = Array.from(this.photoInput.files || []);
      this.photoInput.value = '';
      for (const file of incoming) {
        if (this.files.length >= this.maxFiles) {
          this.showAlert(false, `You can attach up to ${this.maxFiles} photos.`);
          break;
        }
        if (file.size > this.maxFileBytes) {
          this.showAlert(false, `"${file.name}" is too large. Photos must be under 10 MB each.`);
          continue;
        }
        this.files.push(file);
      }
      this.renderPreviews();
    }

    renderPreviews() {
      if (!this.previews) return;
      this.previews.innerHTML = '';
      this.previews.hidden = this.files.length === 0;
      this.files.forEach((file, index) => {
        if (!file.__previewUrl) file.__previewUrl = URL.createObjectURL(file);
        const item = document.createElement('li');
        item.className = 'wf-preview';
        const img = document.createElement('img');
        img.src = file.__previewUrl;
        img.alt = file.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.index = String(index);
        remove.setAttribute('aria-label', `Remove ${file.name}`);
        remove.textContent = '×';
        item.append(img, remove);
        this.previews.append(item);
      });
    }

    // --- Submit ---------------------------------------------------------------

    validateOrderNumber() {
      const value = this.orderInput.value.trim();
      if (this.channel === 'amazon' && !/^\d{3}-\d{7}-\d{7}$/.test(value)) {
        this.orderInput.setCustomValidity(
          'Amazon order IDs look like 113-1234567-1234567. Find yours under "Your Orders" on Amazon.'
        );
      } else {
        this.orderInput.setCustomValidity('');
      }
    }

    async submit(evt) {
      evt.preventDefault();
      this.hideAlert();
      this.validateOrderNumber();
      if (!this.form.reportValidity()) return;

      if (this.pickerActive) {
        this.syncPickerValue();
        if (!this.productInput.value) {
          this.showAlert(false, 'Please select at least one item from your order.');
          return;
        }
      }

      const endpoint = this.dataset.endpoint;
      if (!endpoint) {
        this.showAlert(false, 'This form is not connected yet. Please reach out to us by email instead.');
        return;
      }

      const formData = new FormData(this.form);
      this.files.forEach((file) => formData.append('photos', file, file.name));

      this.submitBtn.classList.add('is-loading');
      this.submitBtn.disabled = true;
      try {
        const res = await fetch(endpoint, { method: 'POST', body: formData });
        const data = await res.json().catch(() => null);
        if (data && data.ok) {
          const channel = this.channel;
          this.form.reset();
          const radio = this.form.querySelector(`[name="channel"][value="${channel}"]`);
          if (radio) radio.checked = true;
          this.files.forEach((f) => f.__previewUrl && URL.revokeObjectURL(f.__previewUrl));
          this.files = [];
          this.renderPreviews();
          this.deactivatePicker();
          this.lookupKey = null;
          this.syncState();
          if (window.turnstile) window.turnstile.reset();
          this.showAlert(true, data.message);
        } else {
          this.showAlert(false, (data && data.message) || 'Something went wrong. Please try again in a minute.');
        }
      } catch (err) {
        this.showAlert(false, 'We could not reach the warranty service. Please check your connection and try again.');
      } finally {
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.disabled = false;
      }
    }

    showAlert(success, text) {
      if (!this.alertEl) return;
      this.alertTextEl.textContent = text;
      this.alertEl.classList.toggle('wf-alert--success', success);
      this.alertEl.classList.toggle('wf-alert--error', !success);
      this.alertEl.classList.toggle('bg-success-bg', success);
      this.alertEl.classList.toggle('text-success-text', success);
      this.alertEl.classList.toggle('bg-error-bg', !success);
      this.alertEl.classList.toggle('text-error-text', !success);
      this.alertEl.hidden = false;
      this.alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.alertEl.focus({ preventScroll: true });
    }

    hideAlert() {
      if (this.alertEl) this.alertEl.hidden = true;
    }

    loadTurnstile() {
      if (document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile"]')) return;
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  }

  customElements.define('warranty-form', WarrantyForm);
}
